import type { SettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';
import { computed } from 'vue';
import { useRoute, useRouter } from '#app/composables/router';
import { useOrganizationEntitlement } from '#layers/access/public';
import { SETTINGS_QUERY_KEY } from '#layers/global-settings/constants/settings-overlay';
import { resolveSettingsSection } from '#layers/global-settings/constants/settings-overlay-sections';

/** The overlay section lives in `?settings=<group>/<section>`; the hash is a native in-panel anchor. */
export function useSettingsOverlay() {
	const route = useRoute();
	const router = useRouter();
	const { can: canOrganization } = useOrganizationEntitlement();

	const requestedSection = computed(() => {
		const value = route.query[SETTINGS_QUERY_KEY];
		return typeof value === 'string' && value.length > 0 ? value : null;
	});
	const activeSection = computed(() => requestedSection.value === null
		? null
		: resolveSettingsSection(requestedSection.value, canOrganization));
	const isOpen = computed(() => requestedSection.value !== null);

	function queryWith(section?: SettingsSection) {
		const query = { ...route.query };
		if (section === undefined) delete query[SETTINGS_QUERY_KEY];
		else query[SETTINGS_QUERY_KEY] = section;
		return query;
	}

	function locationFor(section?: SettingsSection) {
		return { path: route.path, query: queryWith(section), hash: '' };
	}

	function open(section: SettingsSection) {
		const allowedSection = resolveSettingsSection(section, canOrganization);
		// Re-triggering while already open (e.g. the ⌘⇧O shortcut) should switch section, not stack a
		// second `?settings=` entry that would linger and reopen the overlay after close() steps back.
		if (isOpen.value) return navigate(allowedSection);
		router.push(locationFor(allowedSection));
	}

	/** Route location of a section within the open overlay, for links that `navigate()` would follow. */
	function sectionLocation(section: SettingsSection) {
		return locationFor(resolveSettingsSection(section, canOrganization));
	}

	function navigate(section: SettingsSection) {
		router.replace(sectionLocation(section));
	}

	function close() {
		const back = (window.history.state as { back?: unknown } | null)?.back;
		if (typeof back === 'string') router.back();
		else router.replace(locationFor());
	}

	return { activeSection, isOpen, open, navigate, sectionLocation, close };
}
